import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { OrganizationMarketplaceRoom } from '@spike/marketplace-model';
import { Room } from '@spike/room-model';
import { Radio } from 'components/UI/V3/Forms/Radio/Radio';
import DescriptionText from 'components/UI/V3/DescriptionText/DescriptionText';
import { AlertMessage } from 'components/UI/V3/AlertMessage/AlertMessage';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import Service from 'model/Service';
import clsx from 'clsx';
import styles from './RoomsAndSpacesSection.module.scss';

interface RoomsAndSpacesSectionProps {
    className?: string;
    marketplaces: Service['marketplaces'];
    selectedPetTypeIds: Array<string>;
    catalogRooms: Room[];
    olbEnabled?: boolean;
    onChangeMarketplaces: (marketplaces: Service['marketplaces']) => void;
}

const normalizePetTypeId = (petTypeId: string | number): string => String(petTypeId).toLowerCase().trim();

const roomSupportsAllPetTypes = (
    roomPetTypeIds: Array<string | number>,
    selectedPetTypeIds: Array<string>
): boolean => {
    if (!selectedPetTypeIds.length || !roomPetTypeIds?.length) {
        return false;
    }

    const normalizedRoomIds = roomPetTypeIds.map(normalizePetTypeId);
    return selectedPetTypeIds.map(normalizePetTypeId).every(petTypeId => normalizedRoomIds.includes(petTypeId));
};

const filterBoardingRooms = (
    rooms: Array<OrganizationMarketplaceRoom>,
    selectedPetTypeIds: Array<string>
): Array<OrganizationMarketplaceRoom> =>
    rooms
        .filter(room => roomSupportsAllPetTypes(room.petTypeIds ?? [], selectedPetTypeIds))
        .sort((left, right) => left.name.toLocaleLowerCase().localeCompare(right.name.toLocaleLowerCase()));

export const RoomsAndSpacesSection: FC<RoomsAndSpacesSectionProps> = ({
    marketplaces,
    selectedPetTypeIds,
    catalogRooms,
    olbEnabled,
    onChangeMarketplaces,
    className
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const [expandedMarketplaceIds, setExpandedMarketplaceIds] = useState<Array<number>>([]);
    const hasAutoExpandedRef = useRef(false);

    const catalogRoomsById = useMemo(() => {
        const map = new Map<number, Room>();
        catalogRooms.forEach(room => {
            if (room.id != null) {
                map.set(room.id, room);
            }
        });
        return map;
    }, [catalogRooms]);

    const selectedMarketplaces = useMemo(
        () =>
            marketplaces
                .map(serviceMarketplace => {
                    const servicesInfo = organizationServicesInfo.find(
                        info => info.marketplace.id === serviceMarketplace.marketplaceId
                    );

                    if (!servicesInfo) {
                        return null;
                    }

                    return {
                        serviceMarketplace,
                        servicesInfo
                    };
                })
                .filter(
                    (
                        marketplace
                    ): marketplace is {
                        serviceMarketplace: Service['marketplaces'][number];
                        servicesInfo: (typeof organizationServicesInfo)[number];
                    } => marketplace !== null
                )
                .sort((left, right) =>
                    left.servicesInfo.marketplace.name
                        .toLocaleLowerCase()
                        .localeCompare(right.servicesInfo.marketplace.name.toLocaleLowerCase())
                ),
        [organizationServicesInfo, marketplaces]
    );

    useEffect(() => {
        if (hasAutoExpandedRef.current || selectedMarketplaces.length === 0) {
            return;
        }

        setExpandedMarketplaceIds(
            selectedMarketplaces.map(({ serviceMarketplace }) => serviceMarketplace.marketplaceId)
        );
        hasAutoExpandedRef.current = true;
    }, [selectedMarketplaces]);

    const hasAnyBoardingRooms = selectedMarketplaces.some(
        ({ servicesInfo }) => (servicesInfo.rooms?.boarding?.length ?? 0) > 0
    );

    const handleRoomSelect = (marketplaceId: number, roomId: number) => {
        onChangeMarketplaces(
            marketplaces.map(marketplace =>
                marketplace.marketplaceId === marketplaceId ? { ...marketplace, roomIds: [roomId] } : marketplace
            )
        );
    };

    const toggleMarketplaceExpanded = (marketplaceId: number) => {
        setExpandedMarketplaceIds(prev =>
            prev.includes(marketplaceId) ? prev.filter(id => id !== marketplaceId) : [...prev, marketplaceId]
        );
    };

    return (
        <div className={`${styles.switchSection} ${className || ''}`}>
            <div className={styles.roomsLabel}>
                <Label required>Rooms & Spaces</Label>
            </div>
            <DescriptionText text="Choose one room for each location." />

            {!hasAnyBoardingRooms && (
                <div className={styles.noRoomsAvailable}>
                    <AlertMessage
                        icon={faInfoCircle}
                        iconColor="#BAA997"
                        bdColor="#BCB8AE"
                        bgColor="#F8F5F1"
                        className={styles.alert}
                    >
                        <span>
                            You don&apos;t have any boarding rooms set up yet. Please{' '}
                            <a href="/business_settings/info/rooms">add a boarding room</a> before completing your setup
                        </span>
                    </AlertMessage>
                </div>
            )}

            {hasAnyBoardingRooms && (
                <div className={styles.roomsContainer}>
                    {selectedMarketplaces.map(({ serviceMarketplace, servicesInfo }) => {
                        const boardingRooms = filterBoardingRooms(
                            servicesInfo.rooms?.boarding ?? [],
                            selectedPetTypeIds
                        );
                        const selectedRoomId = serviceMarketplace.roomIds?.[0];
                        const selectedRoomName =
                            boardingRooms.find(room => room.id === selectedRoomId)?.name ||
                            (selectedRoomId != null ? catalogRoomsById.get(selectedRoomId)?.name : undefined);
                        const isExpanded = expandedMarketplaceIds.includes(serviceMarketplace.marketplaceId);

                        return (
                            <div
                                key={serviceMarketplace.marketplaceId}
                                className={styles.marketplaceRoomsSection}
                            >
                                <div
                                    className={styles.marketplaceHeader}
                                    onClick={() => toggleMarketplaceExpanded(serviceMarketplace.marketplaceId)}
                                >
                                    <div className={styles.marketplaceHeaderLeft}>
                                        <p className={styles.marketplaceName}>{servicesInfo.marketplace.name}</p>
                                        {selectedRoomName && (
                                            <p className={styles.selectedRoomName}>{selectedRoomName}</p>
                                        )}
                                    </div>
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronUp : faChevronDown}
                                        className={styles.marketplaceChevron}
                                    />
                                </div>
                                <div className={styles.marketplaceNameDivider} />
                                {isExpanded && (
                                    <div className={styles.marketplaceRoomsContent}>
                                        {boardingRooms.length === 0 ? (
                                            <p className={styles.roomsNotDefined}>
                                                No rooms match the selected pet types
                                            </p>
                                        ) : (
                                            <div className={styles.roomsList}>
                                                {boardingRooms.map(room => {
                                                    const roomId = room.id;
                                                    const catalogRoom = catalogRoomsById.get(roomId);
                                                    const rules = catalogRoom?.rules;
                                                    const isDisabled =
                                                        !!olbEnabled && Array.isArray(rules) && rules.length === 0;

                                                    return (
                                                        <div
                                                            key={roomId}
                                                            className={styles.roomRow}
                                                        >
                                                            <Radio
                                                                checked={selectedRoomId === roomId}
                                                                className={clsx({ [styles.disabled]: isDisabled })}
                                                                onChange={() => {
                                                                    if (isDisabled) {
                                                                        return;
                                                                    }
                                                                    handleRoomSelect(
                                                                        serviceMarketplace.marketplaceId,
                                                                        roomId
                                                                    );
                                                                }}
                                                                inlineLabel={room.name}
                                                            />
                                                            {isDisabled && (
                                                                <span className={styles.capacityRuleRequired}>
                                                                    Capacity rule required for Online Booking.
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
